'use client'

import { useRef, useState, type ReactNode } from 'react'
import type { VariantProps } from 'class-variance-authority'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button, buttonVariants } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'

type ActionVariant = VariantProps<typeof buttonVariants>['variant']

export interface ÅDialogAction {
  id: string
  label: string
  loadingLabel?: string
  variant?: ActionVariant
  isDisabled?: boolean
  isLoading?: boolean
  onClick: () => void | Promise<void>
}

interface ÅDialogBaseProps {
  open: boolean
  title: string
  description?: string
  children?: ReactNode
  actions?: ÅDialogAction[]
  isBusy?: boolean
  onOpenChange: (open: boolean) => void
}

interface ÅNormalDialogProps extends ÅDialogBaseProps {
  type?: 'dialog'
  size?: 'md' | 'lg'
}

interface ÅAlertDialogProps extends ÅDialogBaseProps {
  type: 'alert'
  cancelLabel?: string
}

export type ÅDialogProps = ÅNormalDialogProps | ÅAlertDialogProps

interface ActionsProps {
  actions: ÅDialogAction[]
  isBusy: boolean
}

function OverlayActions({ actions, isBusy }: ActionsProps) {
  const lock = useRef(false)
  const [pendingId, setPendingId] = useState<string | null>(null)

  async function run(action: ÅDialogAction) {
    if (lock.current || isBusy || action.isDisabled) return
    lock.current = true
    setPendingId(action.id)
    try {
      await action.onClick()
    } finally {
      lock.current = false
      setPendingId(null)
    }
  }

  return actions.map((action) => {
    const isLoading = action.isLoading || pendingId === action.id
    return (
      <Button
        key={action.id}
        type="button"
        variant={action.variant}
        disabled={
          isBusy || action.isDisabled || action.isLoading || pendingId !== null
        }
        className="min-w-20"
        onClick={() => void run(action)}
      >
        {isLoading && <Spinner aria-hidden="true" data-icon="inline-start" />}
        {isLoading ? (action.loadingLabel ?? action.label) : action.label}
      </Button>
    )
  })
}

function NormalDialog({
  open,
  title,
  description,
  children,
  actions = [],
  isBusy = false,
  size = 'md',
  onOpenChange,
}: ÅNormalDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next, details) => {
        if (!next && isBusy) {
          details.cancel()
          return
        }
        onOpenChange(next)
      }}
    >
      <DialogContent
        showCloseButton={!isBusy}
        className={cn(
          'z-[60] flex max-h-[calc(100dvh-2rem)] min-h-0 flex-col overflow-hidden',
          size === 'lg' ? 'sm:max-w-2xl' : 'sm:max-w-md',
        )}
      >
        <DialogHeader className="shrink-0 pr-8">
          <DialogTitle
            render={<h2 />}
            className="text-xl font-extrabold tracking-tight"
          >
            {title}
          </DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {children && (
          <div
            data-slot="dialog-body"
            className="min-h-0 overflow-x-hidden overflow-y-auto overscroll-contain pb-2"
          >
            {children}
          </div>
        )}
        {actions.length > 0 && (
          <DialogFooter className="shrink-0 flex-row justify-end">
            <OverlayActions actions={actions} isBusy={isBusy} />
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}

function WarningDialog({
  open,
  title,
  description,
  children,
  actions = [],
  cancelLabel = '取消',
  isBusy = false,
  onOpenChange,
}: ÅAlertDialogProps) {
  return (
    <AlertDialog
      open={open}
      onOpenChange={(next, details) => {
        if (!next && isBusy) {
          details.cancel()
          return
        }
        onOpenChange(next)
      }}
    >
      <AlertDialogContent className="z-[60] flex max-h-[calc(100dvh-2rem)] min-h-0 max-w-[calc(100%-2rem)] flex-col overflow-hidden data-[size=default]:sm:max-w-md">
        <AlertDialogHeader className="shrink-0">
          <AlertDialogTitle
            render={<h2 />}
            className="text-xl font-extrabold tracking-tight"
          >
            {title}
          </AlertDialogTitle>
          {description && (
            <AlertDialogDescription>{description}</AlertDialogDescription>
          )}
        </AlertDialogHeader>
        {children && (
          <div
            data-slot="alert-dialog-body"
            className="min-h-0 overflow-x-hidden overflow-y-auto overscroll-contain pb-2"
          >
            {children}
          </div>
        )}
        <AlertDialogFooter className="shrink-0">
          <AlertDialogCancel disabled={isBusy} className="min-w-20">
            {cancelLabel}
          </AlertDialogCancel>
          <OverlayActions actions={actions} isBusy={isBusy} />
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

function AppDialog(props: ÅDialogProps) {
  if (props.type === 'alert') return <WarningDialog {...props} />
  return <NormalDialog {...props} />
}

export { AppDialog as ÅDialog, OverlayActions as ÅOverlayActions }
